import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { residentialUnitReceiptAPI } from '../../services/api';
import { saveAs } from 'file-saver';
import SuccessModal from '../Common/SuccessModal';
import ConfirmModal from '../Common/ConfirmModal';
import ErrorModal from '../Common/ErrorModal';
import './ResidentialUnitReceiptList.css';

const ResidentialUnitReceiptList = () => {
  const { t } = useTranslation();
  const [residentialUnitReceipts, setResidentialUnitReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmData, setConfirmData] = useState(null);

  useEffect(() => {
    loadResidentialUnitReceipts();
  }, [page]);

  const loadResidentialUnitReceipts = async () => {
    try {
      setLoading(true);
      const response = await residentialUnitReceiptAPI.getAll(searchTerm, page, pageSize);
      setResidentialUnitReceipts(response.data.items || []);
      const totalCount = response.data.totalCount || 0;
      setTotalPages(Math.ceil(totalCount / pageSize) || 1);
    } catch (err) {
      setErrorMessage(t('residentialUnitReceipt.list.messages.loadError'));
      setShowErrorModal(true);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    loadResidentialUnitReceipts();
  };

  const handleDelete = (id) => {
    setConfirmAction(() => async () => {
      try {
        await residentialUnitReceiptAPI.delete(id);
        setSuccessMessage(t('residentialUnitReceipt.list.messages.deleteSuccess'));
        setShowSuccessModal(true);
        loadResidentialUnitReceipts();
      } catch (err) {
        setErrorMessage(t('residentialUnitReceipt.list.messages.deleteError'));
        setShowErrorModal(true);
      }
    });
    setConfirmData({
      title: t('common.confirm'),
      message: t('residentialUnitReceipt.list.messages.deleteConfirm'),
      confirmText: t('common.delete'),
      cancelText: t('common.cancel')
    });
    setShowConfirmModal(true);
  };

  const handleBulkDelete = () => {
    if (selectedIds.length === 0) {
      setErrorMessage(t('residentialUnitReceipt.list.messages.selectToDelete'));
      setShowErrorModal(true);
      return;
    }

    setConfirmAction(() => async () => {
      try {
        const response = await residentialUnitReceiptAPI.bulkDelete(selectedIds);
        setSuccessMessage(response.data.message || t('residentialUnitReceipt.list.messages.bulkDeleteSuccess', { count: selectedIds.length }));
        setShowSuccessModal(true);
        setSelectedIds([]);
        loadResidentialUnitReceipts();
      } catch (err) {
        setErrorMessage(err.response?.data?.message || t('residentialUnitReceipt.list.messages.bulkDeleteError'));
        setShowErrorModal(true);
      }
    });
    setConfirmData({
      title: t('common.confirm'),
      message: t('residentialUnitReceipt.list.messages.bulkDeleteConfirm', { count: selectedIds.length }),
      confirmText: t('common.delete'),
      cancelText: t('common.cancel')
    });
    setShowConfirmModal(true);
  };

  const handleConfirm = async () => {
    setShowConfirmModal(false);
    if (confirmAction) {
      await confirmAction();
    }
    setConfirmAction(null);
    setConfirmData(null);
  };

  const handleCancel = () => {
    setShowConfirmModal(false);
    setConfirmAction(null);
    setConfirmData(null);
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(residentialUnitReceipts.map(rv => rv.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter(i => i !== id));
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showExportMenu && !event.target.closest('.export-menu-container')) {
        setShowExportMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showExportMenu]);

  const handleExport = async (format) => {
    try {
      setShowExportMenu(false);
      let response;
      let filename;
      let contentType;

      switch (format) {
        case 'excel':
          response = await residentialUnitReceiptAPI.exportExcel();
          filename = `ResidentialUnitReceipts_${new Date().toISOString().slice(0, 10)}.xlsx`;
          contentType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
          break;
        case 'csv':
          response = await residentialUnitReceiptAPI.exportCsv();
          filename = `ResidentialUnitReceipts_${new Date().toISOString().slice(0, 10)}.csv`;
          contentType = 'text/csv;charset=utf-8';
          break;
        case 'pdf':
          response = await residentialUnitReceiptAPI.exportPdf();
          filename = `ResidentialUnitReceipts_${new Date().toISOString().slice(0, 10)}.pdf`;
          contentType = 'application/pdf';
          break;
        default:
          return;
      }

      const blob = new Blob([response.data], { type: contentType });
      saveAs(blob, filename);
      setSuccessMessage(t('residentialUnitReceipt.list.messages.exportSuccess', { format: format.toUpperCase() }));
      setShowSuccessModal(true);
    } catch (err) {
      console.error('Export error:', err);
      setErrorMessage(t('residentialUnitReceipt.list.messages.exportError', { format: format.toUpperCase() }) + ` ${err.response?.data?.message || err.message}`);
      setShowErrorModal(true);
    }
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="list-view-card">
        <div className="page-actions">
          <div className="page-actions-left">
            <Link to="/residential-unit-receipt/create" className="btn-add">
              + {t('residentialUnitReceipt.list.addNew')}
            </Link>
            <div className="search-container">
              <input
                type="text"
                className="search-input"
                placeholder={t('residentialUnitReceipt.list.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch(e)}
              />
              <button className="search-icon" onClick={handleSearch}>
                <i className="fas fa-search"></i>
              </button>
            </div>
          </div>
          <div className="action-buttons">
            <div className="export-menu-container">
              <button className="action-btn action-btn-export" title={t('common.export')} onClick={() => setShowExportMenu(!showExportMenu)}>
                <i className="fas fa-file-export"></i>
              </button>
              {showExportMenu && (
                <div className="export-menu">
                  <button onClick={() => handleExport('excel')}>
                    <i className="fas fa-file-excel"></i> {t('common.exportExcel')}
                  </button>
                  <button onClick={() => handleExport('csv')}>
                    <i className="fas fa-file-csv"></i> {t('common.exportCsv')}
                  </button>
                  <button onClick={() => handleExport('pdf')}>
                    <i className="fas fa-file-pdf"></i> {t('common.exportPdf')}
                  </button>
                </div>
              )}
            </div>
            <button className="action-btn action-btn-delete" title={t('residentialUnitReceipt.list.bulkDelete')} onClick={handleBulkDelete} disabled={selectedIds.length === 0}>
              <i className="fas fa-trash"></i>
            </button>
          </div>
        </div>

        <ErrorModal
          show={showErrorModal}
          message={errorMessage}
          onClose={() => setShowErrorModal(false)}
        />

        <SuccessModal
          show={showSuccessModal}
          message={successMessage}
          onClose={() => setShowSuccessModal(false)}
        />

        <ConfirmModal
          show={showConfirmModal}
          title={confirmData?.title}
          message={confirmData?.message}
          confirmText={confirmData?.confirmText}
          cancelText={confirmData?.cancelText}
          onConfirm={handleConfirm}
          onCancel={handleCancel}
        />

        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    onChange={handleSelectAll}
                    checked={selectedIds.length === residentialUnitReceipts.length && residentialUnitReceipts.length > 0}
                  />
                </th>
                <th>{t('residentialUnitReceipt.list.columns.id')}</th>
                <th>{t('residentialUnitReceipt.list.columns.contractNumber')}</th>
                <th>{t('residentialUnitReceipt.list.columns.startDate')}</th>
                <th>{t('residentialUnitReceipt.list.columns.endDate')}</th>
                <th>{t('residentialUnitReceipt.list.columns.propertyName')}</th>
                <th>{t('residentialUnitReceipt.list.columns.unitNumber')}</th>
                <th>{t('residentialUnitReceipt.list.columns.firstPartyName')}</th>
                <th>{t('residentialUnitReceipt.list.columns.firstPartyId')}</th>
                <th>{t('residentialUnitReceipt.list.columns.secondPartyName')}</th>
                <th>{t('residentialUnitReceipt.list.columns.secondPartyId')}</th>
                <th>{t('residentialUnitReceipt.list.columns.status')}</th>
                <th>{t('residentialUnitReceipt.list.columns.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {residentialUnitReceipts.length === 0 ? (
                <tr>
                  <td colSpan="12" className="text-center py-4">
                    {t('residentialUnitReceipt.list.messages.noData')}
                  </td>
                </tr>
              ) : (
                residentialUnitReceipts.map((receipt) => (
                  <tr key={receipt.id}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(receipt.id)}
                        onChange={() => handleSelectOne(receipt.id)}
                      />
                    </td>
                    <td>{receipt.id}</td>
                    <td>{receipt.contractNumber}</td>
                    <td>{receipt.startDate ? new Date(receipt.startDate).toLocaleDateString() : '-'}</td>
                    <td>{receipt.endDate ? new Date(receipt.endDate).toLocaleDateString() : '-'}</td>
                    <td>{receipt.propertyName || '-'}</td>
                    <td>{receipt.unitNumber || '-'}</td>
                    <td>{receipt.firstPartyName || '-'}</td>
                    <td>{receipt.firstPartyIdNumber || '-'}</td>
                    <td>{receipt.secondPartyName || '-'}</td>
                    <td>{receipt.secondPartyIdNumber || '-'}</td>
                    <td>
                      <span className={receipt.isActive ? 'status-yes-text' : 'status-no-text'}>
                        {receipt.isActive ? t('common.yes') : t('common.no')}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons-cell">
                        <Link
                          to={`/residential-unit-receipt/view/${receipt.id}`}
                          className="btn btn-sm btn-view"
                          title={t('common.view')}
                        >
                          <i className="fas fa-eye"></i>
                        </Link>
                        <Link
                          to={`/residential-unit-receipt/edit/${receipt.id}`}
                          className="btn btn-sm btn-edit"
                          title={t('common.edit')}
                        >
                          <i className="fas fa-edit"></i>
                        </Link>
                        <button
                          className="btn btn-sm btn-delete"
                          onClick={() => handleDelete(receipt.id)}
                          title={t('common.delete')}
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="pagination-container">
            <ul className="pagination">
              <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                <button className="page-link" onClick={() => setPage(page - 1)} disabled={page === 1}>
                  {t('common.previous') || 'Previous'}
                </button>
              </li>
              {[...Array(totalPages)].map((_, i) => (
                <li key={i + 1} className={`page-item ${page === i + 1 ? 'active' : ''}`}>
                  <button className="page-link" onClick={() => setPage(i + 1)}>
                    {i + 1}
                  </button>
                </li>
              ))}
              <li className={`page-item ${page === totalPages ? 'disabled' : ''}`}>
                <button className="page-link" onClick={() => setPage(page + 1)} disabled={page === totalPages}>
                  {t('common.next')}
                </button>
              </li>
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default ResidentialUnitReceiptList;

